import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';

interface TenantResponse {
  tenant: {
    slug: string;
    displayName: string;
    configSetName: string;
    mailFromDomain: string;
    status: string;
  };
  verification: {
    dkimStatus: string;
    mailFromStatus: string;
    verifiedForSendingStatus: boolean;
  };
}

interface EventItem {
  messageId: string;
  eventType: string;
  receivedAt: string;
}

const STATUS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
  disabled: 'bg-zinc-100 text-zinc-500 ring-1 ring-zinc-500/20',
};
function statusClass(s: string) {
  return STATUS[s] ?? 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
}

function verifyColor(s: string) {
  if (s === 'SUCCESS') return 'text-emerald-600';
  if (s === 'FAILED') return 'text-red-600';
  return 'text-amber-600';
}

export function TenantDetail() {
  const { slug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();

  const tenantQuery = useQuery({
    queryKey: ['tenant', slug],
    queryFn: async () => apiFetch(`/tenants/${slug}`) as Promise<TenantResponse>,
    enabled: Boolean(slug),
  });
  const eventsQuery = useQuery({
    queryKey: ['tenant-events', slug],
    queryFn: async () => apiFetch(`/events?tenant=${slug}`) as Promise<{ items: EventItem[] }>,
    enabled: Boolean(slug),
  });
  const disableMutation = useMutation({
    mutationFn: async () =>
      apiFetch(`/tenants/${slug}/disable`, { method: 'PATCH' }) as Promise<{ tenant: TenantResponse['tenant'] }>,
    onSuccess: async (data) => {
      queryClient.setQueryData(['tenant', slug], (cur: TenantResponse | undefined) =>
        cur ? { ...cur, tenant: data.tenant } : cur,
      );
    },
  });

  if (!slug || !tenantQuery.data) return null;

  const { tenant, verification } = tenantQuery.data;
  const events = eventsQuery.data?.items ?? [];

  const facts = [
    { label: 'Status', value: (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass(tenant.status)}`}>
        {tenant.status}
      </span>
    )},
    { label: 'Config Set', value: <span className="font-mono text-xs">{tenant.configSetName}</span> },
    { label: 'Identity Domain', value: <span className="font-mono text-xs">{tenant.mailFromDomain}</span> },
    { label: 'Verified for Sending', value: verification.verifiedForSendingStatus ? 'Yes' : 'No' },
    { label: 'DKIM', value: <span className={`font-semibold ${verifyColor(verification.dkimStatus)}`}>{verification.dkimStatus}</span> },
    { label: 'MAIL FROM', value: <span className={`font-semibold ${verifyColor(verification.mailFromStatus)}`}>{verification.mailFromStatus}</span> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/tenants" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-700 transition-colors">
          <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5"><path fillRule="evenodd" d="M7.78 12.53a.75.75 0 01-1.06 0L2.47 8.28a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 1.06L4.81 7h7.44a.75.75 0 010 1.5H4.81l2.97 2.97a.75.75 0 010 1.06z" clipRule="evenodd" /></svg>
          Tenants
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">{tenant.displayName}</h1>
            <p className="mt-0.5 font-mono text-sm text-zinc-400">{tenant.slug}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {tenant.status === 'active' ? (
              <Link
                to="/send"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700"
              >
                <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
                  <path d="M8.716 2.177a.75.75 0 00-1.432 0l-5.5 14A.75.75 0 002.5 17h.014a.75.75 0 00.673-.414L5.73 12h4.54l2.543 4.586A.75.75 0 0013.5 17h.014a.75.75 0 00.716-1.023l-5.514-13.8z" />
                </svg>
                Send Test
              </Link>
            ) : null}
            <button
              type="button"
              className="rounded-lg border border-zinc-200 bg-white px-3.5 py-2 text-sm font-medium text-zinc-600 shadow-sm hover:bg-zinc-50 transition-colors"
              onClick={() => tenantQuery.refetch()}
            >
              Re-sync
            </button>
            {tenant.status !== 'disabled' ? (
              <button
                type="button"
                className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2 text-sm font-medium text-red-600 shadow-sm hover:bg-red-100 transition-colors"
                onClick={() => disableMutation.mutate()}
              >
                Disable
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Facts grid */}
      <div className="grid grid-cols-2 gap-px rounded-xl border border-zinc-200 bg-zinc-100 overflow-hidden lg:grid-cols-3">
        {facts.map(({ label, value }) => (
          <div key={label} className="bg-white px-5 py-4">
            <p className="text-xs font-medium uppercase tracking-wider text-zinc-400">{label}</p>
            <div className="mt-1.5 text-sm text-zinc-800">{value}</div>
          </div>
        ))}
      </div>

      {/* Recent events */}
      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">Recent Events</h2>
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
          {events.length ? (
            <table className="min-w-full divide-y divide-zinc-100 text-sm">
              <tbody className="divide-y divide-zinc-100">
                {events.map((e) => (
                  <tr key={`${e.messageId}-${e.receivedAt}`} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-5 py-3 font-medium text-zinc-900">{e.eventType}</td>
                    <td className="px-5 py-3 text-zinc-400 tabular-nums text-xs">{e.receivedAt}</td>
                    <td className="px-5 py-3">
                      <Link to={`/events/${e.messageId}`} className="font-mono text-xs text-indigo-600 hover:underline underline-offset-2">
                        {e.messageId}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-5 py-10 text-center text-sm text-zinc-400">No events yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
